import { useCallback, useContext } from 'react';

import {
  DocumentNode,
  useApolloClient,
  useLazyQuery,
  useMutation,
} from '@apollo/client';
import { GraphQLError } from 'graphql';

import { CashboxContext, useIsCashboxEnabled } from 'cashbox';
import { LocalizationContext } from 'localization';
import { isSandbox, refreshSandboxPaymentViews } from 'sandbox';
import {
  CREATE_PAYMENT,
  IPostPaymentsCount,
  POST_PAYMENT_CONFIRM,
  POST_PAYMENT_COUNT,
  VALIDATE_PAYMENT,
} from 'schemas';
import { openConfirmDialog } from 'services/Confirmation';
import notification from 'services/notificationService';
import { EPaymentItemType, EPaymentMethod } from 'types';

import {
  ICancelPostPaymentFn,
  ICreatePaymentFn,
  ICreatePostPaymentFn,
  IPaymentReturnType,
  IPostPaymentConfirmRequest,
  IPostPaymentConfirmResponse,
} from './interfaces';

export const usePayment = (
  refetchQueries: Array<DocumentNode> | undefined = []
): IPaymentReturnType => {
  const { t } = useContext(LocalizationContext);
  const isCashboxEnabled = useIsCashboxEnabled();
  const { createPayment: createCashboxPayment } = useContext(CashboxContext);
  const apolloClient = useApolloClient();

  const [paymentMutation, { loading }] = useMutation(CREATE_PAYMENT, {
    awaitRefetchQueries: true,
    refetchQueries: [...refetchQueries],
  });

  const [postPaymentMutation, { loading: postPaymentLoading }] = useMutation<
    IPostPaymentConfirmResponse,
    IPostPaymentConfirmRequest
  >(POST_PAYMENT_CONFIRM, {
    awaitRefetchQueries: true,
    refetchQueries: [...refetchQueries],
    update: (cache, { data }) => {
      const isPostPaymentEdited = data?.processPostPayment;

      if (!isPostPaymentEdited) return;

      cache.updateQuery<IPostPaymentsCount>(
        { query: POST_PAYMENT_COUNT },
        (cacheData) => {
          if (
            !cacheData ||
            !cacheData.activeWorkShift ||
            !cacheData.activeWorkShift.payments
          )
            return cacheData;

          const {
            activeWorkShift: { payments },
          } = cacheData;

          return {
            activeWorkShift: {
              payments: payments?.map((payment) =>
                payment.id === isPostPaymentEdited.id
                  ? isPostPaymentEdited
                  : payment
              ),
            },
          };
        }
      );
    },
  });

  const [validatePayment] = useLazyQuery(VALIDATE_PAYMENT, {
    fetchPolicy: 'network-only',
  });

  const createPayment = useCallback<ICreatePaymentFn>(
    async ({
      method,
      promoCodeId,
      clientUUID,
      clientSessionId,
      discountId,
      items,
      shopItems,
      discountValue,
      depositSum,
      cardSum,
      comment,
      availableItems,
    }) => {
      try {
        const { data, error } = await validatePayment({
          variables: {
            input: {
              method,
              items,
              card_sum: cardSum,
              ...(promoCodeId && { promo_code_id: promoCodeId }),
              ...(discountId && { discount_id: discountId }),
              ...(clientUUID && { client_uuid: clientUUID }),
              ...(clientSessionId && { client_session_id: clientSessionId }),
              ...(comment && { comment }),
            },
          },
        });

        if (error || !data) {
          const errorMessage = error
            ? (error.graphQLErrors || [])
                .map((err: GraphQLError) => err.message)
                .join(', ')
            : 'Unknown error occurred';

          throw new Error(errorMessage);
        }

        const printableItems =
          shopItems === null
            ? null
            : (shopItems ?? []).filter(
                (item) =>
                  item.type !== EPaymentItemType.TARIFF ||
                  item.original?.disable_sale_receipt_printing === false
              );

        const itemsToSend =
          printableItems?.length === 0 ? null : printableItems ?? null;

        const shouldPrintReceipt = itemsToSend !== null;
        if (
          (shouldPrintReceipt || depositSum) &&
          isCashboxEnabled &&
          method !== EPaymentMethod.BONUS &&
          method !== EPaymentMethod.DEPOSIT
        ) {
          try {
            await createCashboxPayment({
              method,
              items: itemsToSend ?? [],
              discount: discountValue ?? undefined,
              depositSum,
              cardSum,
              availableItems,
            });
          } catch (cashboxError) {
            throw new Error('kkm server is not found');
          }
        }

        const { data: paymentData } = await paymentMutation({
          variables: {
            input: {
              method,
              items,
              card_sum: cardSum,
              ...(promoCodeId && { promo_code_id: promoCodeId }),
              ...(discountId && { discount_id: discountId }),
              ...(clientUUID && { client_uuid: clientUUID }),
              ...(clientSessionId && { client_session_id: clientSessionId }),
              ...(comment && { comment }),
            },
          },
        });

        if (isSandbox) {
          await refreshSandboxPaymentViews(apolloClient);
        }

        notification.info({
          t,
          type: 'success',
          title: t('shop.success'),
          dismiss: 5,
        });

        return paymentData?.createPayment;
      } catch (error) {
        const errorMessage = (error as Error).message;

        openConfirmDialog(t, 'notify', {
          title:
            errorMessage === 'is booked'
              ? t('shoppingCart.tariff.intersection')
              : t('errors.shop'),
          message: errorMessage,
          acceptTitle: t('ok'),
        });

        throw error;
      }
    },
    [apolloClient, isCashboxEnabled, paymentMutation, validatePayment]
  );

  const createPostPayment = useCallback<ICreatePostPaymentFn>(
    ({
      processPostPaymentId,
      method,
      cash_sum,
      type,
      card_sum,
      sum,
      hostName,
    }) => {
      return new Promise<void>((resolve, reject) => {
        if (isCashboxEnabled) {
          return createCashboxPayment({
            method,
            items: [],
            discount: undefined,
            cardSum: card_sum,
            cashSum: cash_sum,
            postPaymentSum: sum,
          }).then(
            () => resolve(),
            () => reject(new Error('kkm server is not found'))
          );
        }
        resolve();
      })
        .then(() =>
          postPaymentMutation({
            variables: {
              processPostPaymentId,
              input: {
                method,
                cash_sum,
              },
            },
          })
        )
        .then(() => {
          notification.info({
            t,
            type: type === 'payment' ? 'success' : 'info',
            title:
              type === 'payment'
                ? t('shop.success')
                : t('host.notification.seanceFinished'),
            dismiss: 5,
            leftDescription: hostName,
          });
        })
        .catch((error) => {
          if (error.message === 'is booked') {
            notification.info({
              t,
              type: 'error',
              title: t('shoppingCart.tariff.intersection'),
            });

            throw error;
          }

          notification.info({
            t,
            type: 'error',
            title: t('errors.shop'),
            dismiss: 5,
          });

          throw error;
        });
    },
    [isCashboxEnabled]
  );

  const cancelPostPayment = useCallback<ICancelPostPaymentFn>(
    ({ processPostPaymentId, comment, confirm_pass, type, hostName }) => {
      return postPaymentMutation({
        variables: {
          processPostPaymentId,
          input: {
            comment,
            confirm_pass,
          },
        },
      })
        .then(() => {
          notification.info({
            t,
            type: type === 'payment' ? 'success' : 'info',
            title:
              type === 'payment'
                ? t('shop.success')
                : t('host.notification.seanceFinished'),
            dismiss: 5,
            leftDescription: hostName,
          });
        })
        .catch((error) => {
          if (error.message === 'is booked') {
            notification.info({
              t,
              type: 'error',
              title: t('shoppingCart.tariff.intersection'),
            });

            throw error;
          }

          if (error.message === 'invalid password') {
            notification.info({
              t,
              type: 'error',
              title: t('errors.password.incorrect'),
            });

            throw error;
          }

          notification.info({
            t,
            type: 'error',
            title: t('errors.shop'),
            dismiss: 5,
          });

          throw error;
        });
    },
    [isCashboxEnabled]
  );

  return {
    createPayment,
    loading,
    createPostPayment,
    postPaymentLoading,
    cancelPostPayment,
  };
};
